import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Autocomplete,
  Pagination,
  TextField,
  Typography,
  Box,
  useMediaQuery,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { debounce } from "lodash";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { AppContext } from "../../App";
import { UserProfile } from "../../types";
import axiosInstance from "../../utils/axiosInstance";
import { translate } from "../../utils/localisation";
import { PageHeader } from "../headers/PageHeader";

export function UserProfileList() {
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);

  const pageSize = 25;
  const appContext = useContext(AppContext);
  const navigate = useNavigate();

  const theme = useTheme();
  const isTable = useMediaQuery(theme.breakpoints.up("md"));

  const handleChange = (_: React.ChangeEvent<unknown>, value: number) => {
    appContext.setPage(value - 1);
  };

  const debounceFetch = useMemo(
    () =>
      debounce(
        async (
          input: string,
          page: number,
          abortController: AbortController
        ) => {
          try {
            const res = await axiosInstance.get("/admin/user-profile/", {
              signal: abortController.signal,
              params: { pageSize, page, email: input },
            });
            appContext.setUserProfiles(res.data.profiles);
            appContext.setNumberOfResults(res.data.count);
          } catch {
            /* ignore */
          } finally {
            setLoading(false);
          }
        },
        600
      ),
    [appContext]
  );

  useEffect(() => {
    appContext.setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  useEffect(() => {
    const abortController = new AbortController();
    setLoading(true);
    debounceFetch(searchInput, appContext.page, abortController);
    return () => {
      abortController.abort();
      debounceFetch.cancel();
    };
  }, [searchInput, appContext.page, debounceFetch]);

  const goTo = (userId: string) => navigate(`/user-profile/${userId}`);

  return (
    <Box
      sx={{
        // lock the page from horizontal overflow
        overflowX: "hidden",
        maxWidth: { xs: "100%" },
        mx: "auto",
        width: "100%",
        px: { xs: 1.5, sm: 2 },
        py: 1,
      }}
    >
      <PageHeader title={translate("user-profiles-screen.title-text")} />

      <Autocomplete
        id="user-search"
        freeSolo
        fullWidth
        options={appContext.userProfiles.map(u => u.email)}
        onInputChange={(_, v) => setSearchInput(v)}
        filterOptions={x => x}
        renderInput={params => (
          <TextField
            {...params}
            name="userSearch"
            label={translate(
              "user-profiles-screen.search-box-placeholder-text"
            )}
            size="small"
          />
        )}
        sx={{ mb: 2 }}
      />

      <Box sx={{ position: "relative" }}>
        {isTable ? (
          <TableContainer
            component={Paper}
            sx={{ borderRadius: 2, overflowX: "auto" }}
          >
            <Table stickyHeader aria-label="user profiles table" size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="left">
                    {translate("user-profiles-screen.table-col-1-title-text")}
                  </TableCell>
                  <TableCell align="left">
                    {translate("user-profiles-screen.table-col-2-title-text")}
                  </TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {appContext.userProfiles.map((user: UserProfile) => (
                  <TableRow
                    hover
                    key={user.id}
                    onClick={() => goTo(user.id)}
                    role="button"
                    tabIndex={0}
                    sx={{
                      cursor: "pointer",
                      "&:last-child td, &:last-child th": { border: 0 },
                    }}
                  >
                    <TableCell align="left" sx={{ wordBreak: "break-word" }}>
                      {user.email}
                    </TableCell>
                    <TableCell align="left" sx={{ wordBreak: "break-all" }}>
                      {user.id}
                    </TableCell>
                    <TableCell align="right">
                      <OpenInNewIcon fontSize="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Paper
            variant="outlined"
            sx={{ borderRadius: 2, overflow: "hidden" }}
          >
            <List disablePadding sx={{ width: "100%" }}>
              {appContext.userProfiles.map((user: UserProfile, idx) => (
                <Box key={user.id}>
                  <ListItemButton
                    onClick={() => goTo(user.id)}
                    sx={{
                      width: "100%",
                      py: 1.25,
                      alignItems: "flex-start",
                      // prevent flex children from forcing overflow
                      "& .MuiListItemSecondaryAction-root": { right: 8 },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        mr: 1,
                        mt: 0.25,
                        flex: "0 0 auto",
                      }}
                    >
                      <ChevronRightIcon />
                    </ListItemIcon>

                    <ListItemText
                      sx={{
                        m: 0,
                        minWidth: 0, // <-- allow text to shrink within row
                      }}
                      primaryTypographyProps={{
                        variant: "body1",
                        fontWeight: 600,
                        noWrap: false,
                        sx: { wordBreak: "break-word" },
                      }}
                      secondaryTypographyProps={{
                        variant: "caption",
                        color: "text.secondary",
                        sx: { wordBreak: "break-all" },
                      }}
                      primary={user.email}
                      secondary={user.id}
                    />

                    <Box sx={{ flex: "0 0 auto", ml: 1 }}>
                      <OpenInNewIcon fontSize="small" />
                    </Box>
                  </ListItemButton>

                  {idx < appContext.userProfiles.length - 1 && (
                    <Divider component="li" />
                  )}
                </Box>
              ))}
            </List>
          </Paper>
        )}

        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              bgcolor: "rgba(255,255,255,0.72)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2,
              borderRadius: 2,
              backdropFilter: "blur(1px)",
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: { xs: "space-between", sm: "flex-end" },
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ order: { xs: 2, sm: 1 } }}
        >
          {translate("user-profiles-screen.table-page-lable-text")}:{" "}
          {appContext.page + 1}
        </Typography>

        <Pagination
          sx={{ order: { xs: 1, sm: 2 } }}
          color="primary"
          size="small"
          shape="rounded"
          count={Math.ceil(appContext.numberOfResults / pageSize)}
          page={appContext.page + 1}
          onChange={handleChange}
        />
      </Box>
    </Box>
  );
}
